import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import SectionWrapper from './ui/SectionWrapper';
import { CORE_TOOLS, goToApp } from '../constants';
import useLandingAssets from '../hooks/useLandingAssets';
import { first, isUploadedFile, toEmbedUrl } from '../lib/landingAssets';

// Pick the best preview media URL + type from an API asset; null if none.
function pickPreview(asset) {
  if (!asset) return null;
  if (asset.media_type === 'image') return { kind: 'image', url: asset.url };
  if (asset.media_type === 'video') {
    if (isUploadedFile(asset.url)) return { kind: 'video', url: asset.url, poster: asset.thumbnail_url };
    return { kind: 'iframe', url: toEmbedUrl(asset.external_url || asset.url), poster: asset.thumbnail_url };
  }
  if (asset.media_type === 'url') return { kind: 'iframe', url: toEmbedUrl(asset.external_url || asset.url), poster: asset.thumbnail_url };
  if (asset.media_type === 'carousel') {
    const it = (asset.items || [])[0];
    if (!it) return null;
    if (it.type === 'video') return { kind: 'video', url: it.url };
    if (it.type === 'url') return { kind: 'iframe', url: toEmbedUrl(it.url) };
    return { kind: 'image', url: it.url };
  }
  return null;
}

const MEDIA_CLS = 'w-full h-full object-cover transition-transform duration-700 group-hover:scale-105';

const ToolMedia = ({ preview, fallback, title }) => {
  if (preview?.kind === 'video') return <video src={preview.url} poster={preview.poster} autoPlay muted loop playsInline className={MEDIA_CLS} />;
  if (preview?.kind === 'iframe' && !preview.poster) {
    return <iframe src={preview.url} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />;
  }
  return <img src={preview?.poster || preview?.url || fallback} alt={title} loading="lazy" className={MEDIA_CLS} />;
};

const CoreTools = () => {
  const sections = useLandingAssets();

  return (
    <SectionWrapper id="tools" eyebrow="Powerful tools in one workspace" title="Everything you need to create, in one place.">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {CORE_TOOLS.map((tool, index) => {
          const apiAsset = tool.apiKey ? first(sections?.[tool.apiKey]) : null;
          const title = apiAsset?.title || tool.title;
          const description = apiAsset?.description || tool.description;
          const key = tool.apiKey || `tool-${tool.id}`;

          return (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
              onClick={goToApp}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToApp(); } }}
              className="group cursor-pointer rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-brand/40 hover:shadow-[0_20px_40px_-24px_rgba(124,58,237,0.6)] transition-[box-shadow,border-color] duration-300 flex flex-col"
              data-testid={`core-tool-card-${key}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-900">
                <ToolMedia preview={pickPreview(apiAsset)} fallback={tool.image} title={title} />
                <span className="absolute top-3 left-3 w-8 h-8 rounded-lg bg-black/50 backdrop-blur text-white flex items-center justify-center"><tool.icon size={15} /></span>
              </div>
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-display font-bold text-ink text-[15px]">{title}</h3>
                <p className="text-[13px] text-slate-500 mt-1 leading-snug flex-1">{description}</p>
                <span className="mt-3 text-[13px] font-semibold text-brand inline-flex items-center gap-1 group-hover:gap-2 transition-[gap]" data-testid={`core-tool-try-${key}`}>
                  Learn more <ArrowRight size={14} />
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </SectionWrapper>
  );
};

export default CoreTools;
